import { FC, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFeatureFlag } from 'utils/hooks/useFeatureFlag';
import {
  APPLY_BUTTON,
  DYNAMIC_CONTENT_REPORTING_SNIPPET,
  DYNAMIC_HEATMAP,
  FETCH_ERRORS as e,
} from '../constants';
import { selectAppliedMediums, selectError } from '../selectors';
import { useCampaignAndMetricFiltersContext } from './CampaignAndMetricFiltersContext';
import { TabContext } from './TabContext';

export const TabContextProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const [selectedTab, setSelectedTab] = useState('overview');
  const { selectedMediums, selectedCrossMetrics } = useCampaignAndMetricFiltersContext();
  const isDynamicHeatmapEnabled = useFeatureFlag(DYNAMIC_HEATMAP);
  const isApplyButtonEnabled = useFeatureFlag(APPLY_BUTTON);
  const isDCRSEnabled = useFeatureFlag(DYNAMIC_CONTENT_REPORTING_SNIPPET);
  const errors = useSelector(selectError);
  const appliedMediums = useSelector(selectAppliedMediums);
  const activeMediums = isApplyButtonEnabled ? appliedMediums : selectedMediums;
  const isEmailMediumActive = activeMediums.includes('Email');
  const isEmailMediumSelected = selectedMediums.includes('Email');
  const showTabs = selectedCrossMetrics?.length > 0 && selectedMediums?.length > 0;

  const hideTabContentBackground = useMemo(
    () =>
      ['overview', 'timeSeries', 'segments', 'campaigns', 'devices', 'snippets'].includes(
        selectedTab,
      ) ||
      [e.FETCH_CAMPAIGNS, e.FETCH_X_MED_AGG_STATS].some((err) => Object.keys(errors).includes(err)),
    [selectedTab, errors],
  );

  useEffect(() => {
    // Set 'overview' as the selected tab if the following conditions are met
    if (
      (['location', 'devices', 'snippets'].includes(selectedTab) && !isEmailMediumSelected) ||
      (selectedTab === 'snippets' && !isDynamicHeatmapEnabled)
    ) {
      setSelectedTab('overview');
    }
  }, [isEmailMediumSelected, isDynamicHeatmapEnabled]);

  return (
    <TabContext.Provider
      value={{
        selectedTab,
        hideTabContentBackground,
        showTabs,
        showDeviceTab: isEmailMediumActive,
        showLocationTab: isEmailMediumActive,
        showSnippetTab: isDCRSEnabled && isEmailMediumSelected,
        setSelectedTab,
      }}
    >
      {children}
    </TabContext.Provider>
  );
};
